/* eslint-disable max-lines */

import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { BasicButton, ButtonType, Icon, IconSize, SegmentedControl } from "@gaming/xds-react-js";
import { ApplicationLaunch } from "@gaming/xds-react-js-icons";

import { FormattedText } from "../../../components/FormattedText";
import { useStrings } from "../../../localization/localizationHelpers";
import { OnboardingProgram } from "../../../models/status";
import { OnboardingPageAction } from "../../../telemetry/models";
import { logPageAction } from "../../../telemetry/pageAction";
import FrequentlyAskedQuestion from "../FrequentlyAskedQuestion/FrequentlyAskedQuestion";
import {
  ProgramProps,
  useCreatorsProgramLandingPageProgramConfig,
  useIdXboxProgramLandingPageProgramConfig,
} from "./programConfigurations";
import * as styles from "./XboxDeveloperPrograms.module.css";

// This is used to calculate the segmented control label size based on the bigger label chars length
const labelLengthMultiplier = 10.25;
export const XboxDeveloperPrograms = () => {
  const [
    landingPageXboxDeveloperProgramsTitle,
    landingPageXboxDeveloperProgramsDescription,
    landingPageXboxDeveloperProgramsTabID,
    landingPageXboxDeveloperProgramsTabCreators,
    landingPageFrequentlyAskedQuestionsTitle,
    landingPageApplyNowIdXboxProgram,
    landingPageJoinNowCreatorsProgramCTA,
  ] = useStrings(
    "landingPageXboxDeveloperProgramsTitle",
    "landingPageXboxDeveloperProgramsDescription",
    "landingPageXboxDeveloperProgramsTabID",
    "landingPageXboxDeveloperProgramsTabCreators",
    "landingPageFrequentlyAskedQuestionsTitle",
    "landingPageApplyNowIdXboxProgram",
    "landingPageJoinNowCreatorsProgramCTA"
  );

  const [selectedProgram, setSelectedProgram] = useState(OnboardingProgram.IdXboxProgram);
  const history = useHistory();
  const mapValueOptionToEnum = new Map([
    [0, OnboardingProgram.IdXboxProgram],
    [1, OnboardingProgram.CreatorsProgram],
  ]);
  const isFirstRender = useRef(true);
  const idXboxProgramConfig = useIdXboxProgramLandingPageProgramConfig();
  const creatorsProgramConfig = useCreatorsProgramLandingPageProgramConfig();

  useEffect(() => {
    const segmentedControlComponent = document.querySelectorAll('[role="tablist"]');
    const nodes = segmentedControlComponent[0]?.children ?? null;
    if (segmentedControlComponent != null && nodes?.length >= 2) {
      // Tab 1
      nodes[0].setAttribute("role", "tab");
      nodes[0].setAttribute("id", "tab1");
      nodes[0].setAttribute("key", "tab1");

      // Tab 2
      nodes[1].setAttribute("role", "tab");
      nodes[1].setAttribute("id", "tab2");
      nodes[1].setAttribute("key", "tab2");
    }
  }, []);

  useEffect(() => {
    const segmentedControlComponent = document.querySelectorAll('[role="tablist"]');
    const nodes = segmentedControlComponent[0]?.children ?? null;
    if (isFirstRender.current || nodes == null) {
      return;
    }
    if (
      segmentedControlComponent == null ||
      segmentedControlComponent[0] == null ||
      segmentedControlComponent[0]?.children?.length < 2
    ) {
      return;
    }

    const select = (childNode: number) => {
      nodes[childNode].setAttribute("tabIndex", "0");
      nodes[childNode].setAttribute("aria-selected", "true");
      (nodes[childNode] as HTMLElement).click();
      (nodes[childNode] as HTMLElement).focus();
    };

    const unselect = (childNode: number) => {
      nodes[childNode].setAttribute("aria-selected", "false");
      nodes[childNode].setAttribute("tabIndex", "-1");
    };

    const selectIdXboxProgramTab = () => {
      unselect(0);
      select(1);
    };

    const selectCreatorsProgramTab = () => {
      unselect(1);
      select(0);
    };

    if (selectedProgram === OnboardingProgram.CreatorsProgram) {
      selectIdXboxProgramTab();
    } else if (selectedProgram === OnboardingProgram.IdXboxProgram) {
      selectCreatorsProgramTab();
    }
  }, [selectedProgram]);

  const programProps: ProgramProps =
    selectedProgram === OnboardingProgram.IdXboxProgram
      ? idXboxProgramConfig
      : creatorsProgramConfig;

  const onProgramSelection = (value: number) => {
    if (selectedProgram === mapValueOptionToEnum.get(value)) {
      return;
    }
    isFirstRender.current = false;
    switch (value) {
      case 1:
        setSelectedProgram(OnboardingProgram.CreatorsProgram);
        break;
      case 0:
      default:
        setSelectedProgram(OnboardingProgram.IdXboxProgram);
        break;
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") {
      isFirstRender.current = false;
      setSelectedProgram(OnboardingProgram.IdXboxProgram);
    }
    if (event.key === "ArrowRight") {
      isFirstRender.current = false;
      setSelectedProgram(OnboardingProgram.CreatorsProgram);
    }
  };

  return (
    <div>
      <div>
        <h2 className={styles.title}>{landingPageXboxDeveloperProgramsTitle}</h2>
        <p className={styles.description}>{landingPageXboxDeveloperProgramsDescription}</p>
      </div>
      <div className={styles.programSelectionRow}>
        <SegmentedControl
          id={"segmentedControlId"}
          aria-controls={"segmentedControlId"}
          role={"tablist"}
          value={0}
          labels={[
            landingPageXboxDeveloperProgramsTabID,
            landingPageXboxDeveloperProgramsTabCreators,
          ]}
          backplateClassName={styles.programSelectionBackplate}
          buttonWidth={
            Math.max(
              landingPageXboxDeveloperProgramsTabID.length,
              landingPageXboxDeveloperProgramsTabCreators.length
            ) * labelLengthMultiplier
          }
          buttonClassName={styles.segmentedControlButton}
          height={40}
          className={styles.segmentedControl}
          textClassName={styles.programLabel}
          onKeyDown={e => handleKeyPress(e)}
          onValueChange={onProgramSelection}
        />
      </div>
      <div>
        <div>
          <div className={styles.xboxProgram}>
            <div className={styles.xboxProgramDetails}>
              <h3 className={styles.xboxProgramTitle}>{programProps.title}</h3>
              <div className={styles.xboxProgramBadgeRow}>
                {programProps.badges.map((badge, index) => (
                  <div
                    className={styles.xboxProgramBadgeFrame}
                    key={`xboxProgramBadge${index}${badge}`}
                  >
                    <div className={styles.xboxProgramBadge}>{badge}</div>
                  </div>
                ))}
              </div>
              <p className={styles.xboxProgramDescription}>
                <FormattedText text={programProps.description} />
              </p>
              <BasicButton
                aria-label={
                  selectedProgram === OnboardingProgram.IdXboxProgram
                    ? landingPageApplyNowIdXboxProgram
                    : landingPageJoinNowCreatorsProgramCTA
                }
                as={"a"}
                tabIndex={0}
                className={styles.xboxProgramApplyNow}
                buttonType={ButtonType.Secondary}
                onClick={() => {
                  logPageAction(OnboardingPageAction.ContinueApplicationButton);
                  if (programProps.callToAction.isExternalUrl) {
                    window.open(programProps.callToAction.url, "_blank");
                  } else {
                    history.push(programProps.callToAction.url);
                  }
                }}
              >
                <p>{programProps.callToAction.text}</p>
              </BasicButton>
            </div>
            <div className={styles.xboxProgramImage}>
              <img src={programProps.image.url} alt={programProps.image.alt} />
            </div>
          </div>
          <div className={styles.xboxProgramCardsContainer}>
            {programProps.cards.map((card, index) => (
              <div key={index} className={styles.xboxProgramWrapper}>
                <img
                  className={styles.xboxProgramCardImage}
                  src={card.image.url}
                  alt={card.image.alt}
                />
                <div className={styles.xboxProgramCardInfo}>
                  <h4 className={styles.xboxProgramCardTitle}>{card.title}</h4>
                  <p className={styles.xboxProgramCardDescription}>{card.description}</p>
                </div>
                <div className={styles.xboxProgramCardLink}>
                  <a href={card.link!.url} target="_new" aria-label={card.link?.ariaLabel}>
                    {card.link!.text}{" "}
                    <Icon aria-hidden={true} icon={ApplicationLaunch} iconSize={IconSize.Custom} />
                  </a>
                </div>
                <div className={styles.xboxProgramSeparatorBlock} />
              </div>
            ))}
          </div>
          <div className={styles.xboxProgramFaqs}>
            <h2 className={styles.xboxProgramFaqsTitle}>
              {landingPageFrequentlyAskedQuestionsTitle}
            </h2>
            {programProps.faqs.map((faq, index) => (
              <FrequentlyAskedQuestion
                key={faq.question}
                id={index}
                question={faq.question}
                answer={faq.answer}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
